import * as React from "react";
import { Inbox, CircleAlert, CircleCheck, Info, LoaderCircle, type LucideIcon } from "lucide-react";
import { ControlledSelect } from "./controlled-select";

/** Tiny classnames joiner. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/**
 * Pill controls: the rounded, 40px-tall buttons that sit side by side in toolbar
 * rows and the header - Filters, Save search, the List/Map toggle, Log in and
 * Sign up.
 *
 * One definition because they are always SEEN together, and drift was exactly
 * the bug: the same row carried a 44px rounded-xl Filters button, a 36px pill,
 * a 37px toggle and a 36px Sign up. Compose `pill.base` with one variant:
 *
 *   className={cx(pill.base, pill.outline)}
 *
 * The List/Map segmented toggle uses `pill.height` on its track so the outer
 * pill matches the buttons beside it.
 */
const pillShape =
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";

export const pill = {
  height: "h-10",
  // Two SIZES rather than one base plus overrides. Stacking `h-12` on top of a
  // base that already sets `h-10` leaves the winner to stylesheet order, not
  // class order - so a size is chosen once, here.
  base: `${pillShape} h-10 px-4 text-sm`,
  /** Hero-scale primary actions. */
  baseLg: `${pillShape} h-12 px-6 text-base`,
  // Font weight lives in the variant, not the shape, for the same reason as the
  // sizes: primary is semibold, and a shape-level font-medium would conflict.
  outline: "border border-border bg-background font-medium hover:border-foreground",
  primary: "bg-primary font-semibold text-primary-foreground hover:bg-primary-hover",
  ghost: "font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
} as const;

const inputBase =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50";

export function Button({
  className,
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  children,
  disabled,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "danger" | "link";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  fullWidth?: boolean;
}) {
  // Flat fills, no resting shadow. A button is already the highest-contrast
  // thing in its row; a drop shadow on top of that is the pairing that reads
  // as dated. Hover darkens the fill rather than fading it - see --primary-hover.
  const variants = {
    primary: "bg-primary text-primary-foreground hover:bg-primary-hover",
    secondary: "bg-secondary text-secondary-foreground hover:bg-subtle",
    outline: "border border-border bg-background hover:bg-muted",
    ghost: "text-muted-foreground hover:bg-muted hover:text-foreground",
    danger: "bg-danger-solid text-danger-solid-foreground hover:brightness-95",
    link: "text-primary underline-offset-4 hover:underline px-0 py-0",
  };
  const sizes = { sm: "h-8 px-3 text-xs", md: "h-10 px-4 text-sm", lg: "h-12 px-6 text-base" };
  return (
    <button
      disabled={disabled || loading}
      className={cx(
        "relative inline-flex items-center justify-center gap-2 rounded-md font-medium transition-[color,background-color,border-color,transform] duration-150",
        "active:scale-[0.98] motion-reduce:active:scale-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:opacity-50 disabled:pointer-events-none",
        variant !== "link" && sizes[size],
        variants[variant],
        fullWidth && "w-full",
        className,
      )}
      {...props}
    >
      {loading && <LoaderCircle size={16} className="animate-spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cx(inputBase, className)} {...props} />;
  },
);

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} className={cx(inputBase, "min-h-24", className)} {...props} />;
  },
);

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    // A controlled select needs a client effect to survive React's form reset
    // (see controlled-select.tsx); an uncontrolled one stays server-renderable.
    if (props.value !== undefined) {
      return (
        <ControlledSelect ref={ref} className={cx(inputBase, className)} {...props}>
          {children}
        </ControlledSelect>
      );
    }
    return (
      <select ref={ref} className={cx(inputBase, className)} {...props}>
        {children}
      </select>
    );
  },
);

export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-sm font-medium">
        {label}
        {required && <span className="ml-0.5 text-danger" aria-hidden="true">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

export function Checkbox({
  label,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input type="checkbox" className={cx("h-4 w-4 rounded border-input accent-primary", className)} {...props} />
      {label}
    </label>
  );
}

export function Badge({
  children,
  tone = "neutral",
  className,
}: {
  children: React.ReactNode;
  tone?: "neutral" | "success" | "warning" | "danger" | "info" | "primary";
  className?: string;
}) {
  const tones = {
    neutral: "bg-muted text-muted-foreground",
    success: "bg-success-bg text-success",
    warning: "bg-warning-bg text-warning",
    danger: "bg-danger-bg text-danger",
    info: "bg-info-bg text-info",
    primary: "bg-accent text-accent-foreground",
  };
  return (
    <span className={cx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium", tones[tone], className)}>
      {children}
    </span>
  );
}

/* ----- Surfaces ----- */

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cx(
        // Flat by default: a hairline border and NO resting shadow. Pass
        // shadow-e2/e3 explicitly for a card that genuinely floats over
        // something. See the elevation note in globals.css.
        "rounded-xl border border-border bg-card text-card-foreground",
        // Dark mode: shadows are invisible on dark, so depth comes from a subtle
        // inner top-edge highlight instead (the Linear/Vercel elevation trick).
        "dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]",
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("flex flex-col gap-1 p-5 pb-0", className)} {...props} />;
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cx("text-base font-semibold leading-tight", className)} {...props} />;
}

export function CardDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cx("text-sm text-muted-foreground", className)} {...props} />;
}

export function CardBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("p-5", className)} {...props} />;
}

export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("flex items-center gap-2 p-5 pt-0", className)} {...props} />;
}

/* ----- Layout ----- */

export function Container({
  className,
  size = "lg",
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { size?: "sm" | "md" | "lg" | "xl" }) {
  // `xl` was byte-identical to `lg`, so callers asking for a wider container
  // silently got the same width. It is now genuinely wider.
  const sizes = { sm: "max-w-2xl", md: "max-w-4xl", lg: "max-w-6xl", xl: "max-w-7xl" };
  return <div className={cx("mx-auto w-full px-4 pt-2", sizes[size], className)} {...props} />;
}

export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("flex flex-wrap items-start justify-between gap-4", className)}>
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        {description && <p className="text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cx("border-border", className)} />;
}

/* ----- Feedback ----- */

export function Spinner({ className, size = 20 }: { className?: string; size?: number }) {
  return (
    <LoaderCircle
      size={size}
      className={cx("animate-spin text-muted-foreground", className)}
      role="status"
      aria-label="Loading"
    />
  );
}

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("animate-pulse rounded-md bg-muted", className)} aria-hidden="true" {...props} />;
}

export function Avatar({
  src,
  name,
  size = 40,
  className,
}: {
  src?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
}) {
  const initials = (name ?? "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span
      className={cx(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent text-accent-foreground font-medium",
        className,
      )}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt={name ?? ""} width={size} height={size} className="h-full w-full object-cover" />
      ) : (
        <span aria-hidden="true">{initials || "?"}</span>
      )}
    </span>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon: Icon = Inbox,
}: {
  title: string;
  body?: string;
  action?: React.ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-border p-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Icon className="text-muted-foreground" size={24} aria-hidden="true" />
      </div>
      <p className="mt-3 font-medium">{title}</p>
      {body && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Alert({
  tone = "error",
  title,
  children,
}: {
  tone?: "error" | "success" | "warning" | "info";
  title?: string;
  children: React.ReactNode;
}) {
  const tones = {
    error: { cls: "bg-danger-bg text-danger", Icon: CircleAlert, role: "alert" as const },
    success: { cls: "bg-success-bg text-success", Icon: CircleCheck, role: "status" as const },
    warning: { cls: "bg-warning-bg text-warning", Icon: CircleAlert, role: "status" as const },
    info: { cls: "bg-info-bg text-info", Icon: Info, role: "status" as const },
  };
  const { cls, Icon, role } = tones[tone];
  return (
    <div role={role} className={cx("flex items-start gap-2 rounded-md px-3 py-2 text-sm", cls)}>
      <Icon size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
      <div>
        {title && <p className="font-medium">{title}</p>}
        <span>{children}</span>
      </div>
    </div>
  );
}
